import React from 'react';
import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { reset_password } from '../../Redux/ActionCreator';
import ResetForm from '../common/reset_form';



const MISSING_LINK_MESSAGE =
	'Open the reset link from your email to set a new password. If the link has expired, request a new one from the Forgot password page.';
const MISMATCH_MESSAGE = 'Both passwords must match.';
const EMPTY_MESSAGE = 'Enter a new password.';
const SUCCESS_MESSAGE = 'Password updated. Taking you to sign in\u2026';
// Long enough to read one short line, short enough not to feel like a hang.
const SUCCESS_DWELL_MS = 1400;

const Reset = () => {
	const [sending, setSending] = useState(false);
	// Only navigate on a success THIS mount produced. `password_reset.message`
	// survives in the store, so keying the redirect on it alone bounces a
	// founder who simply revisits the page after an earlier reset.
	const [submitted, setSubmitted] = useState(false);
	const [succeeded, setSucceeded] = useState(false);
	const [localError, setLocalError] = useState('');
	// initialize useDispatch and useSelector hook
	const dispatch = useDispatch();
	const r_stat = useSelector(prev => prev.password_reset);
	const [enteredPassword, setEnteredPassword ] = useState({password: "", confirm: ""});
	const navigate = useNavigate();

	const params = new URLSearchParams(window.location.search);
	const userId = params.get("userId");
	// Minted by the backend's validateToken redirect and echoed back on the
	// POST. Without it the reset endpoint had no way to tell the founder who
	// clicked her emailed link from anyone who knew her user id.
	const token = params.get("token");

	const formHandle = (e) => {
		e.preventDefault();
		// The password is already changed and we are only holding the
		// confirmation on screen before navigating; a second click here would
		// re-POST a token the server has just consumed.
		if (succeeded) return;
		setLocalError('');

		// TRIMMED, because sign-in and sign-up trim and this form did not — so a
		// password pasted with a trailing space was stored with the space and
		// then compared against the trimmed value at login, locking the user out
		// of the password they had just set. The server normalizes too; this
		// keeps the value in the box and the value we send in agreement.
		const password = (enteredPassword.password || '').trim();
		const confirm = (enteredPassword.confirm || '').trim();

		if (!password) {
			setLocalError(EMPTY_MESSAGE);
			return;
		}
		if (password !== confirm) {
			setLocalError(MISMATCH_MESSAGE);
			return;
		}
		if (!userId) {
			setLocalError(MISSING_LINK_MESSAGE);
			return;
		}

		setSending(true);
		setSubmitted(true);
		// The fields are deliberately NOT cleared here. They used to be wiped on
		// submit, before the response arrived, so a refused reset looked exactly
		// like a successful one and the user lost what they had typed.
		dispatch(reset_password({ password, confirmPassword: confirm, userId, token }));
	};

	useEffect(() => {
		if (!submitted || succeeded) return;
		if (r_stat.isloading) return;
		if (r_stat.errMess) {
			setSending(false);
			return;
		}
		// Anything that settled without an error IS success. Keying on
		// `r_stat.message` instead leaves the user on a spinner forever
		// whenever the payload is falsy — and it is: the reset returns
		// `user.AcctType`, handleResponse maps a falsy value to null, and a
		// Facebook-created account has no AcctType at all. The reducer clears
		// errMess on success, so its absence is the reliable signal.
		setEnteredPassword({password: "", confirm: ""});
		setSucceeded(true);
	}, [submitted, succeeded, r_stat.isloading, r_stat.errMess]);

	// Confirm on the page the user is already looking at, THEN move them.
	// Navigating the instant the request settled took them to a login screen
	// with no word about whether the password had actually changed — the same
	// silence this whole change exists to remove, one screen over.
	//
	// Inline rather than a toast because the confirmation belongs where they are
	// already looking, and because it depends on no container plumbing at all.
	useEffect(() => {
		if (!succeeded) return;
		const t = setTimeout(() => navigate("/login"), SUCCESS_DWELL_MS);
		return () => clearTimeout(t);
	}, [succeeded, navigate]);

	// The server's sentence wins — it is the only one that knows whether the
	// link expired, was superseded, or names an account that signs in with a
	// provider instead.
	const errorMessage = succeeded
		? ''
		: localError || (submitted ? r_stat.errMess : '') || (!userId ? MISSING_LINK_MESSAGE : '');

	return (
		<div className="h-screen relative max-w-[100vw] overflow-x-hidden">
			<div className={`absolute z-10 w-full gap-x-4 gap-y-4 justify-center md:top-[50%] md:translate-y-[-50%] lg:max-w-[876px] xl:max-w-[1024px] left-[50%] translate-x-[-50%] top-[80px] flex lg:gap-x-0 lg:justify-between flex-col md:flex-row items-center px-4`}>

				<div className='flex flex-col gap-y-4 items-center'>
					<svg width="79" height="67" viewBox="0 0 79 67" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M25.1001 0.959991C27.6462 0.959991 30.088 1.97142 31.8883 3.77177C33.6887 5.57212 34.7001 8.01391 34.7001 10.56C34.7001 12.336 34.1721 13.968 33.3561 15.36C33.3561 15.36 29.9001 14.4 25.1001 14.4C20.3001 14.4 16.8441 15.36 16.8441 15.36C16.0281 13.968 15.5001 12.336 15.5001 10.56C15.5001 8.01391 16.5115 5.57212 18.3119 3.77177C20.1122 1.97142 22.554 0.959991 25.1001 0.959991ZM53.9001 0.959991C59.2281 0.959991 63.5001 5.23199 63.5001 10.56C63.5001 12.336 62.9721 13.968 62.1561 15.36C62.1561 15.36 58.7001 14.4 53.9001 14.4C49.1001 14.4 45.6441 15.36 45.6441 15.36C44.8281 13.968 44.3001 12.336 44.3001 10.56C44.3001 5.23199 48.5721 0.959991 53.9001 0.959991ZM39.5001 66.72C48.2067 66.7269 56.6972 64.0091 63.7815 58.9477C70.8658 53.8862 76.1887 46.7349 79.0041 38.496L72.6681 36.288C67.8201 50.496 54.4761 60 39.5001 60C24.5241 60 11.1801 50.496 6.33209 36.288L-0.00390625 38.496C2.81147 46.7349 8.13437 53.8862 15.2187 58.9477C22.3029 64.0091 30.7935 66.7269 39.5001 66.72Z" fill="#F8F8F8" />
					</svg>

					<p className="text-white font-poppins text-[24px] md:text-[32px] font-semibold leading-[30px] md:leading-[36px] tracking-normal flex flex-col gap-y-10 max-w-[380px]">Don’t worry!
						Everybody forgets
						their password.
						<span className="font-poppins text-[14px] md:text-[24px] font-[500] leading-[20px] md:leading-[28px] tracking-normal">
							Create a new password
							without stress.
						</span></p>
				</div>
				<ResetForm handle={formHandle} cred={setEnteredPassword} confirm={enteredPassword.confirm} password={enteredPassword.password} error={errorMessage} success={succeeded ? SUCCESS_MESSAGE : ''} sending={sending && !succeeded} />
			</div>


			<div className={`w-full h-screen grid custom-grid`}>

				<div className="w-full max-h-[340px] md:max-h-max md:h-full bg-[#5843BD]"></div>

				<div className="relative h-screen overflow-hidden -z-1000">
					<svg width="851" height="851" viewBox="0 0 1028 992" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-[-30%] right-[-30%] -z-10">
						<g opacity="0.1" filter="url(#filter0_f_3436_5351)">
							<circle cx="739.5" cy="252.5" r="425.5" fill="#777DEA" />
						</g>
						<defs>
							<filter id="filter0_f_3436_5351" x="0" y="-487" width="1479" height="1479" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
								<feFlood flood-opacity="0" result="BackgroundImageFix" />
								<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
								<feGaussianBlur stdDeviation="157" result="effect1_foregroundBlur_3436_5351" />
							</filter>
						</defs>
					</svg>

					<svg width="1065" height="1065" viewBox="0 0 1201 1024" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-[-25%] right-[-20%] -z-10">
						<g opacity="0.1" filter="url(#filter0_f_3436_5429)">
							<circle cx="847.5" cy="695.5" r="533.5" fill="#FF6633" />
						</g>
						<defs>
							<filter id="filter0_f_3436_5429" x="0" y="-152" width="1695" height="1695" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
								<feFlood flood-opacity="0" result="BackgroundImageFix" />
								<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
								<feGaussianBlur stdDeviation="157" result="effect1_foregroundBlur_3436_5429" />
							</filter>
						</defs>
					</svg>
				</div>
			</div>
		</div>
	);
};

export default Reset;
